import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getConfigurationBySenior } from '../../lib/supabase';

interface ManagedModePinBannerProps {
  seniorId: string;
  seniorName: string;
  /** Opens the Managed Mode drawer so the caregiver can set the PIN. */
  onSetPin: () => void;
  /** Bumped by the parent after a PIN is saved, to re-check. */
  refreshKey?: number;
}

/**
 * Managed Mode ships ON, but nothing in the signup wizard asks for a PIN — by
 * design (wizard v2 is locked). The server therefore refuses activation with 428
 * while Managed Mode is on and no PIN exists, so that a senior can never end up
 * with a launcher-locked phone nobody can unlock.
 *
 * Without this banner that gate is a dead end: the caregiver pays, gets a code,
 * types it into the phone, and is told to "set the PIN in your dashboard" with
 * nothing on the dashboard mentioning a PIN. This makes the required action
 * visible BEFORE they try to activate.
 *
 * Renders nothing unless a PIN is genuinely missing.
 */
export const ManagedModePinBanner: FC<ManagedModePinBannerProps> = ({
  seniorId,
  seniorName,
  onSetPin,
  refreshKey = 0,
}) => {
  const { t } = useTranslation();
  const [needsPin, setNeedsPin] = useState(false);

  useEffect(() => {
    if (!seniorId) return;
    let cancelled = false;
    (async () => {
      try {
        const config = await getConfigurationBySenior(seniorId);
        if (cancelled) return;
        const managedOn = config?.managed_mode_enabled ?? true;
        const hasPin = Boolean(config?.managed_mode_pin);
        setNeedsPin(managedOn && !hasPin);
      } catch (err) {
        // Stay silent on failure — a spurious "action required" banner would be
        // worse than none. The server gate still protects the senior.
        console.warn('[ManagedModePinBanner] could not check PIN state:', err);
        if (!cancelled) setNeedsPin(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [seniorId, refreshKey]);

  if (!needsPin) return null;

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 sm:p-5 flex items-start gap-3">
      <span className="material-symbols-outlined text-amber-600 flex-shrink-0" style={{ fontSize: '22px' }}>
        lock_open
      </span>
      <div className="flex-1 min-w-0">
        <p className="font-display text-[15px] font-semibold text-amber-900">
          {t('managedModePinBanner.title')}
        </p>
        <p className="text-sm text-amber-800 mt-0.5">
          {t('managedModePinBanner.body', { name: seniorName })}
        </p>
      </div>
      <button
        onClick={onSetPin}
        className="flex-shrink-0 px-4 py-2 rounded-xl bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 transition-colors"
      >
        {t('managedModePinBanner.cta')}
      </button>
    </div>
  );
};

export default ManagedModePinBanner;
